import FocusTrap from 'focus-trap-react';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { twJoin } from 'tailwind-merge';

import {
  AnimatedBackdrop,
  type AnimatedBackdropHandler,
} from './components/AnimatedBackdrop';
import { Trigger } from './components/Trigger';
import { useAnimationTracker } from './useAnimationTracker';

type OverlayProps = {
  children: ReactNode;
  /**
   * The container where the overlay will be rendered. If not provided, the
   * overlay will be rendered on the document body. You can set this to null to
   * render as normal component.
   */
  container?: HTMLElement | null;
  /**
   * Disable the focus trap. This is useful when the overlay is not on the
   * document body and the focus should be managed by the parent.
   */
  focusTrapDisabled?: boolean;
  /**
   * Called when the overlay is triggered by click or keyboard escape.
   *
   * **Caution**: Don't use this to unmount the overlay. Use `onDismissComplete`
   * instead.
   */
  onDismiss?(): void;
  /**
   * Called when the overlay is completely dismissed, after the animation ends.
   *
   * Must be used to unmount the overlay. If the overlay is unmounted before the
   * animation is completed, the animation will be interrupted and the overlay
   * will disappear instantly.
   */
  onDismissComplete?(): void;
  /** This will change the color. */
  variant?: ComponentProps<typeof AnimatedBackdrop>['variant'];
};

export type OverlayHandler = {
  /** Start the dismiss animation. */
  startDismissAnimation(): void;
};

/**
 * A blocking layer on top of the current page content.
 *
 * The overlay layer will cover all content except for the provided children.
 * This is generally used for modals, dropdowns and other UI elements that need
 * to appear above the page while requiring immediate action from the user.
 *
 * - The focus will be automatically [trapped][focus-trap] inside of the
 *   children
 * - The body scroll will automatically be [locked][body-scroll-lock]
 * - The parent will handle whether the overlay is rendered and dismissed
 *
 * The overlay is animated and will fade in and out. The fade in animation is
 * triggered when the overlay is mounted. The fade out animation is triggered
 * when a the user clicks on the overlay or presses the escape. The fadeOut
 * animation can be controlled by the method `dismiss` provided by the ref. It's
 * useful when the overlay is dismissed by a button outside of the overlay.
 *
 * The overlay shouldn't be unmounted before the fade out animation is
 * completed. The `onDismissComplete` callback is called after the animation. If
 * the overlay is unmounted before the animation is completed, the animation
 * will be interrupted and the `onDismissComplete` callback will not be called.
 * The `onDismiss` callback is called directly when the overlay is triggered, so
 * it's useful to handle another thing like closing a modal to synchronize
 * animations.
 *
 * [body-scroll-lock]: https://github.com/rick-liruixin/body-scroll-lock-upgrade
 * [focus-trap]: https://github.com/focus-trap/focus-trap
 */
export const Overlay = forwardRef<OverlayHandler, OverlayProps>(
  (
    {
      children,
      container = document.body,
      focusTrapDisabled = false,
      onDismiss,
      onDismissComplete,
      variant = 'light',
    },
    ref,
  ) => {
    const backdrop = useRef<AnimatedBackdropHandler>(null);
    const { markDismissAnimationComplete, markDismissAnimationStarted } =
      useAnimationTracker();

    const startDismissAnimation = () => {
      markDismissAnimationStarted();
      backdrop.current?.startDismissAnimation();
    };

    useImperativeHandle(ref, () => ({ startDismissAnimation }));

    const handleTrigger = () => {
      startDismissAnimation();
      onDismiss?.();
    };

    const handleDismissComplete = () => {
      markDismissAnimationComplete();
      onDismissComplete?.();
    };

    const isOnDocumentBody = container === document.body;

    const markup = (
      <FocusTrap active={!focusTrapDisabled || isOnDocumentBody}>
        <div
          className={twJoin(
            isOnDocumentBody ? 'fixed' : 'absolute',
            'inset-0 bottom-0 left-0 right-0 top-0 z-[100] h-full w-full',
          )}
        >
          <AnimatedBackdrop
            className="absolute z-10"
            variant={variant}
            onDismissComplete={handleDismissComplete}
            ref={backdrop}
          />
          <Trigger className="absolute z-20" onTrigger={handleTrigger}>
            {children}
          </Trigger>
        </div>
      </FocusTrap>
    );

    return container ? createPortal(markup, container) : markup;
  },
);

Overlay.displayName = 'Overlay';
